/* import __COLOCATED_TEMPLATE__ from './fin-audience-component.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';

interface AiContentSegment {
  id: string;
  name: string;
  nameWithFallback: string;
  rulesetId: number;
  createdAt: Date;
}

interface Args {
  selectedFinAudience: string[] | null;
  filterContent: (filterProperty: string, value: any) => void;
  showDelete?: boolean;
  removeFromActiveFilter?: (filterId: string) => void;
  activeFilter?: string | null;
}

export default class FinAudienceComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    // Load segments if feature flag is enabled and not already loading
    if (this.app.canUseFeature?.('workflows-fin-audiences')) {
      if (!this.aiContentSegmentsService.isLoading) {
        this.aiContentSegmentsService.loadSegments();
      }
    }
  }

  get app() {
    return this.appService.app;
  }

  get isOpen() {
    return this.args.activeFilter === 'fin-audience';
  }

  get finAudiences() {
    // Only return segments if feature flag is enabled
    if (!this.app.canUseFeature?.('workflows-fin-audiences')) {
      return [];
    }

    // Get AI content segments (Fin Audiences) from the service
    let aiSegments = this.aiContentSegmentsService.segments || [];

    return aiSegments.map((segment: AiContentSegment) => ({
      text: segment.nameWithFallback || segment.name,
      value: segment.id,
      isSelected: this.args.selectedFinAudience?.includes(segment.id) || false,
    }));
  }

  get name() {
    let selectedSegmentIds = this.args.selectedFinAudience;

    if (!selectedSegmentIds?.length) {
      return this.intl.t('operator.workflows.filter.fin-audience.any');
    }

    if (selectedSegmentIds.length === 1) {
      // Try to find the segment name
      let segmentId = selectedSegmentIds[0];
      let segment = this.findSegmentById(segmentId);
      if (segment) {
        return this.intl.t('operator.workflows.filter.fin-audience.single', {
          name: segment.name,
        });
      }
    }

    return this.intl.t('operator.workflows.filter.fin-audience.multiple', {
      count: selectedSegmentIds?.length || 0,
    });
  }

  findSegmentById(segmentId: string): AiContentSegment | null {
    let aiSegments = this.aiContentSegmentsService.segments;
    if (aiSegments) {
      return aiSegments.find((segment: AiContentSegment) => segment.id === segmentId) || null;
    }
    return null;
  }

  @action
  filterByFinAudiences(selectedSegmentIds: string[]) {
    let filterValue = selectedSegmentIds.length > 0 ? selectedSegmentIds : null;
    this.args.filterContent('selectedFinAudience', filterValue);
  }

  @action
  removeFilter() {
    this.args.filterContent('selectedFinAudience', null);
    this.args.removeFromActiveFilter?.('fin-audience');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Outbound::Filters::FinAudienceComponent': typeof FinAudienceComponent;
  }
}
